import { Patient } from "fhir/r4";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import { Button, Form, InputGroup, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

/**
 * Has to be used inside a Form component. Use configuration displayed below to enable validation.
 * 
 * <Form noValidate validated={validated}>
 * 
 * @param props properties for the component.
 * @returns the rendered Elements.
 */
const PatientForm: FunctionComponent<{
    submitTrigger?: number,
    resetTrigger?: number,
    displayButtons?: boolean,
    patientSubmit: (patient: Patient) => void
}> = (props) => {
    const { t } = useTranslation();
    const submitPatientRef = useRef<() => void>(() => undefined);
    submitPatientRef.current = generateAndSubmitPatient;

    useEffect(() => {
        if (props.resetTrigger) {
            onReset();
        }
    }, [props.resetTrigger]);

    useEffect(() => {
        if (props.submitTrigger) {
            submitPatientRef.current();
        }
    }, [props.submitTrigger]);

    const [form, setForm] = useState({
        name: '',
        surname: '',
        ins: '',
        ins_key: '',
        birthdate: '',
        birthplace: '',
        gender: 'unknown',
    });

    const handleChange = (event: { target: { name: any; value: any; }; }) => {
        setForm({ ...form, [event.target.name]: event.target.value });
    }

    const onSubmit = (event: any) => {
        const formtv = event.currentTarget;
        if (formtv.checkValidity() === true) {
            generateAndSubmitPatient();
        }
    }

    function generateAndSubmitPatient() {
        if (props.patientSubmit) {

            const patient = {
                "resourceType": "Patient",
                "id": "1",
                "extension": [
                    {
                        "url": "http://interopsante.org/fhir/StructureDefinition/FrPatientIdentReliability",
                        "extension": [
                            {
                                "url": "identityReliability",
                                "valueCoding": {
                                    "system": "http://interopsante.org/fhir/CodeSystem/fr-v2-0445",
                                    "code": "VIDE"
                                }
                            }
                        ]
                    },
                ],
                "active": true,
            } as Patient;

            patient.name = [
                {
                    "use": "official",
                    "family": form.name,
                    "given": [
                        form.surname
                    ]
                }
            ];

            patient.identifier = [
                {
                    "use": "official",
                    "type": {
                        "coding": [
                            {
                                "system": "http://interopsante.org/CodeSystem/fr-v2-0203",
                                "code": "INS-NIR",
                                "display": "NIR"
                            }
                        ]
                    },
                    "system": "urn:oid:1.2.250.1.213.1.4.8",
                    "value": form.ins + form.ins_key
                }
            ];

            patient.extension?.push(
                {
                    "url": "http://hl7.org/fhir/StructureDefinition/patient-birthPlace",
                    "valueAddress": {
                        "district": form.birthplace
                    }
                }
            );

            patient.birthDate = form.birthdate;
            patient.gender = form.gender as Patient["gender"];

            props.patientSubmit(patient);
        }
    }

    const onReset = () => {
        setForm({
            name: '',
            surname: '',
            ins: '',
            ins_key: '',
            birthdate: '',
            birthplace: '',
            gender: 'unknown',
        });
    }

    return <><Form.Group
            className="col"
        >
            <Form.Label>{t("forms.common.name")} :</Form.Label>
            <Form.Control
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                className="form-control"
                required />
            <Form.Control.Feedback type="invalid">
                {t("forms.patient.invalidName")}
            </Form.Control.Feedback>
        </Form.Group>
        <Form.Group className="col">
            <Form.Label>{t("forms.common.firstName")} :</Form.Label>
            <Form.Control
                type="text"
                name="surname"
                value={form.surname}
                onChange={handleChange}
                className="form-control"
                required />
            <Form.Control.Feedback type="invalid">
                {t("forms.patient.invalidFirstName")}
            </Form.Control.Feedback>
        </Form.Group>

        <Row className="mb-3">
            <Form.Group className="col">
            <Form.Label>{t("forms.patient.ins")} :</Form.Label>
                <InputGroup hasValidation>
                    <Form.Control
                        type="text"
                        pattern="[0-9]{13}"
                        name="ins"
                        value={form.ins}
                        onChange={handleChange}
                        placeholder={t("forms.patient.ins")}
                        required />
                    <Form.Control
                        type="text"
                        pattern="[0-9]{2}"
                        name="ins_key"
                        value={form.ins_key}
                        onChange={handleChange}
                        placeholder={t("forms.patient.insKey")}
                        required />
                    <Form.Control.Feedback type="invalid">
                        {t("forms.patient.invalidIns")}
                    </Form.Control.Feedback>
                </InputGroup>
            </Form.Group>
        </Row>
        <Form.Group className="col">
            <Form.Label>{t("forms.common.birthDate")} :</Form.Label>
            <Form.Control
                type="date"
                name="birthdate"
                value={form.birthdate}
                onChange={handleChange}
                className="form-control"
                required
            />
            <Form.Control.Feedback type="invalid">
                {t("forms.patient.invalidBirthDate")}
            </Form.Control.Feedback>
        </Form.Group>

        <Form.Group
            className="col"
        >
            <Form.Label>{t("forms.patient.birthPlace")} :</Form.Label>
            <Form.Control
                type="text"
                name="birthplace"
                pattern="[0-9]{5}"
                value={form.birthplace}
                onChange={handleChange}
                className="form-control"
                required />
            <Form.Control.Feedback type="invalid">
                {t("forms.patient.invalidBirthPlace")}
            </Form.Control.Feedback>
        </Form.Group>
        <Form.Group className="col col-sm-6">
            <Form.Label>{t("forms.common.gender")} :</Form.Label>
            <Form.Select
                className="form-control"
                name="gender"
                value={form.gender}
                onChange={handleChange}
            >
                {(["unknown", "female", "male", "other"] as const).map((gender) => <option key={gender} value={gender}>{t(`forms.common.genderOptions.${gender}`)}</option>)}
            </Form.Select>
        </Form.Group>
        {props.displayButtons &&
            <Row className="mb-3">
                <Form.Group className="col">

                    <Button
                        className="button"
                        variant="danger"
                        onClick={onSubmit}
                    >
                        {t("forms.common.submit")}
                    </Button>
                    <Button
                        className="button"
                        variant="secondary"
                        onClick={onReset}
                    >
                        {t("forms.common.reset")}
                    </Button>
                </Form.Group>
            </Row>
        }
    </>;
};

export default PatientForm;
